// 应用编排：启动配置 → 恢复会话 → 流式问答 → 侧栏/手势/语音/微信/繁简。
import { api } from "./api";
import { initGestures } from "./gestures";
import { scriptLabel, scriptToggleTitle, setScript, t } from "./i18n";
import { ChatStream } from "./sse";
import { store, uuid } from "./store";
import type { ClientConfig, Script, Source, Turn } from "./types";
import * as ui from "./ui";
import { createVoice, voiceSupported, type VoiceController } from "./voice";
import { setupWeChat, updateShare } from "./wechat";

const userId = store.userId();
let script: Script = store.script();
let sessionId = store.currentSessionId();
let turns: Turn[] = store.loadTurns(sessionId);
let stream: ChatStream | null = null;
let busy = false;
let cfg: ClientConfig | null = null;
let ctxTurnId: string | null = null;
let voice: VoiceController | null = null;

const input = ui.$<HTMLTextAreaElement>("input");
const sendBtn = ui.$<HTMLButtonElement>("btn-send");

// ---------------- 启动 ----------------
async function boot(): Promise<void> {
  setScript(script);
  ui.applyStaticText();
  ui.$("btn-script").textContent = scriptLabel();
  ui.$("btn-script").setAttribute("title", scriptToggleTitle());
  ui.$("btn-script").setAttribute("aria-label", scriptToggleTitle());
  document.body.dataset.loading = "0";

  // 先用本地缓存即时渲染（弱网/离线也能开局，FR-3.3）
  renderAll();
  ui.renderSessions([], sessionId, selectSession, deleteSession);
  input.value = store.draft(sessionId);
  autoGrow();
  updateSendState();

  wireEvents();
  initGestures({
    isPanelOpen: () =>
      document.body.classList.contains("drawer-open") ||
      document.body.classList.contains("refs-open"),
    openDrawer: () => togglePanel("drawer-open", true),
    closePanels: closePanels,
    onLongPress: openCtxMenu,
  });

  // 语音能力
  if (voiceSupported()) {
    voice = createVoice({
      onResult: (text) => insertVoiceText(text),
      onError: (key) => ui.toast(t(key as never)),
      getScript: () => script,
    });
    wireVoice();
  } else {
    ui.$("btn-voice").style.display = "none";
  }

  // 拉取服务端配置与会话列表（在线时）
  try {
    cfg = await api.config();
    ui.setCorpusStats(cfg);
    if (cfg.default_script && !localStorageHasScript()) {
      script = cfg.default_script;
      setScript(script);
      ui.applyStaticText();
      ui.$("btn-script").textContent = scriptLabel();
      ui.$("btn-script").setAttribute("title", scriptToggleTitle());
      ui.$("btn-script").setAttribute("aria-label", scriptToggleTitle());
      renderAll();
      ui.setCorpusStats(cfg);
    }
    void refreshSessions();
    void refreshSchool();
    // 微信集成（默认关闭则 no-op）
    void setupWeChat(cfg, defaultShare());
  } catch {
    ui.showNetBanner(t("net_offline"));
  }
}

function localStorageHasScript(): boolean {
  try {
    return !!window.localStorage.getItem("zr.script");
  } catch {
    return false;
  }
}

function renderAll(): void {
  ui.renderThread(turns, { onRegen: regenerate, onExample: sendExample, onChipClick: focusChip });
  ui.renderRefs(lastSources());
}

function lastSources(): Source[] {
  for (let i = turns.length - 1; i >= 0; i--) {
    const s = turns[i].sources;
    if (turns[i].role === "assistant" && s && s.length) return s;
  }
  return [];
}

function persist(): void {
  store.saveTurns(sessionId, turns);
}

// ---------------- 发送 / 流式 ----------------
function sendExample(text: string): void {
  input.value = text;
  void send();
}

async function send(): Promise<void> {
  const question = input.value.trim();
  if (!question || busy) return;

  input.value = "";
  store.setDraft(sessionId, "");
  autoGrow();

  const uTurn: Turn = { id: uuid(), role: "user", content: question };
  const aTurn: Turn = {
    id: uuid(),
    role: "assistant",
    content: "",
    sources: [],
    streaming: true,
    question,
  };
  turns.push(uTurn, aTurn);
  setBusy(true);
  ui.renderThread(turns, { onRegen: regenerate, onExample: sendExample, onChipClick: focusChip });
  persist();

  await runStream(aTurn, question);
}

async function regenerate(): Promise<void> {
  if (busy) return;
  // 找最后一条 assistant 与其问题
  let idx = -1;
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].role === "assistant") {
      idx = i;
      break;
    }
  }
  if (idx < 0) return;
  const question = turns[idx].question || findPrevUser(idx);
  if (!question) return;
  const aTurn: Turn = {
    id: uuid(),
    role: "assistant",
    content: "",
    sources: [],
    streaming: true,
    question,
  };
  turns[idx] = aTurn; // 替换重生
  turns = turns.slice(0, idx + 1);
  setBusy(true);
  ui.renderThread(turns, { onRegen: regenerate, onExample: sendExample, onChipClick: focusChip });
  persist();
  await runStream(aTurn, question);
}

function findPrevUser(idx: number): string {
  for (let i = idx - 1; i >= 0; i--) if (turns[i].role === "user") return turns[i].content;
  return "";
}

async function runStream(aTurn: Turn, question: string): Promise<void> {
  stream?.abort();
  const convId = uuid();
  let gotDelta = false;
  let persistTick = 0;

  stream = new ChatStream(
    { question, userId, sessionId, convId, script },
    {
      onMeta: (m) => {
        aTurn.sources = m.sources || [];
        aTurn.grounded = m.grounded;
        ui.renderRefs(aTurn.sources);
        persist();
      },
      onDelta: (text) => {
        gotDelta = true;
        aTurn.content += text;
        ui.patchStreamingBubble(aTurn);
        if (++persistTick % 8 === 0) persist();
      },
      onDone: () => {
        aTurn.streaming = false;
        finishStream(aTurn);
      },
      onStatus: (s) => {
        if (s === "open") ui.showNetBanner(null);
        else if (s === "reconnecting") ui.showNetBanner(t("net_reconnect"));
        else if (s === "rate_limited") {
          aTurn.streaming = false;
          aTurn.error = true;
          if (!gotDelta) aTurn.content = t("rate_limited");
          ui.toast(t("rate_limited"));
          finishStream(aTurn);
        } else if (s === "error") {
          aTurn.streaming = false;
          aTurn.error = true;
          if (!gotDelta) aTurn.content = t("net_offline");
          ui.showNetBanner(null);
          finishStream(aTurn);
        }
      },
    },
  );
  await stream.run();
}

function finishStream(aTurn: Turn): void {
  setBusy(false);
  ui.renderThread(turns, { onRegen: regenerate, onExample: sendExample, onChipClick: focusChip });
  ui.renderRefs(aTurn.sources?.length ? aTurn.sources : lastSources());
  persist();
  void refreshSessions();
  void refreshSchool();
  if (aTurn.question) updateShare(answerShare(aTurn));
}

function setBusy(v: boolean): void {
  busy = v;
  updateSendState();
}

// ---------------- 会话 / 偏好 ----------------
async function refreshSessions(): Promise<void> {
  try {
    const sessions = await api.sessions(userId);
    ui.renderSessions(sessions, sessionId, selectSession, deleteSession);
  } catch {
    ui.renderSessions([], sessionId, selectSession, deleteSession);
  }
}

async function refreshSchool(): Promise<void> {
  try {
    const h = await api.history(userId, sessionId);
    ui.setSchoolBadge((h.prefs?.dominant_school as string) || null);
  } catch {
    /* noop */
  }
}

async function selectSession(sid: string): Promise<void> {
  if (sid === sessionId && turns.length) {
    closePanels();
    return;
  }
  stream?.abort();
  setBusy(false);
  sessionId = sid;
  store.setCurrentSession(sid);
  // 先用本地缓存，再尝试服务端补全
  turns = store.loadTurns(sid);
  renderAll();
  closePanels();
  try {
    const h = await api.history(userId, sid);
    if (h.turns?.length) {
      turns = h.turns.map((x) => ({
        id: uuid(),
        role: x.role,
        content: x.content,
        sources: x.sources || [],
        grounded: x.role === "assistant" ? (x.sources?.length ?? 0) > 0 : undefined,
      }));
      persist();
      renderAll();
    }
    ui.setSchoolBadge((h.prefs?.dominant_school as string) || null);
  } catch {
    /* 离线：用本地缓存 */
  }
  input.value = store.draft(sid);
  autoGrow();
}

async function deleteSession(sid: string): Promise<void> {
  stream?.abort();
  store.clearTurns(sid);
  if (sid === sessionId) {
    setBusy(false);
    sessionId = store.newSessionId();
    store.setCurrentSession(sessionId);
    turns = [];
    input.value = "";
    store.setDraft(sessionId, "");
    autoGrow();
    updateSendState();
    renderAll();
    ui.setSchoolBadge(null);
  }
  try {
    await api.deleteSession(userId, sid);
  } catch {
    /* 离线时至少删除本地缓存；下次在线仍可继续使用新会话 */
  }
  await refreshSessions();
}

function newSession(): void {
  stream?.abort();
  setBusy(false);
  sessionId = store.newSessionId();
  store.setCurrentSession(sessionId);
  turns = [];
  renderAll();
  closePanels();
  void refreshSessions();
  ui.setSchoolBadge(null);
  input.value = "";
  autoGrow();
  input.focus();
}

// ---------------- 经卷对照 ----------------
function focusChip(chunkId: string): void {
  if (window.matchMedia("(max-width: 860px)").matches) togglePanel("refs-open", true);
  ui.focusRef(chunkId);
}

// ---------------- 抽屉 ----------------
function togglePanel(cls: "drawer-open" | "refs-open", on?: boolean): void {
  const other = cls === "drawer-open" ? "refs-open" : "drawer-open";
  document.body.classList.remove(other);
  const willOpen = on ?? !document.body.classList.contains(cls);
  document.body.classList.toggle(cls, willOpen);
  ui.$("scrim").hidden = !document.body.classList.contains(cls);
}
function closePanels(): void {
  document.body.classList.remove("drawer-open", "refs-open");
  ui.$("scrim").hidden = true;
}

// ---------------- 长按菜单 ----------------
function openCtxMenu(bubble: HTMLElement, x: number, y: number): void {
  ctxTurnId = bubble.dataset.turnId || null;
  ui.showCtxMenu(x, y);
}

function ctxTurn(): Turn | undefined {
  return turns.find((tn) => tn.id === ctxTurnId);
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      /* noop */
    }
    ta.remove();
  }
}

// ---------------- 语音 ----------------
function insertVoiceText(text: string): void {
  input.value = (input.value ? input.value + " " : "") + text;
  store.setDraft(sessionId, input.value);
  autoGrow();
  input.focus();
}

function wireVoice(): void {
  const btn = ui.$("btn-voice");
  const hint = ui.$("voice-hint");
  let recording = false;
  let canceling = false;
  let startY = 0;

  const begin = (clientY: number) => {
    if (busy || !voice) return;
    recording = true;
    canceling = false;
    startY = clientY;
    btn.classList.add("recording");
    hint.hidden = false;
    voice.start();
  };
  const end = () => {
    if (!recording || !voice) return;
    recording = false;
    btn.classList.remove("recording");
    hint.hidden = true;
    if (canceling) voice.cancel();
    else voice.stop();
  };

  btn.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    begin(e.clientY);
    btn.setPointerCapture?.(e.pointerId);
  });
  btn.addEventListener("pointermove", (e) => {
    if (!recording) return;
    canceling = startY - e.clientY > 60;
    hint.textContent = canceling ? t("voice_cancel") : t("voice_hint");
  });
  btn.addEventListener("pointerup", () => end());
  btn.addEventListener("pointercancel", () => {
    canceling = true;
    end();
  });
}

// ---------------- 繁简切换 ----------------
function toggleScript(): void {
  script = script === "zh-Hant" ? "zh-Hans" : "zh-Hant";
  store.setScript(script);
  setScript(script);
  ui.applyStaticText();
  ui.$("btn-script").textContent = scriptLabel();
  ui.$("btn-script").setAttribute("title", scriptToggleTitle());
  ui.$("btn-script").setAttribute("aria-label", scriptToggleTitle());
  if (cfg) ui.setCorpusStats(cfg);
  renderAll();
  void refreshSessions();
  void refreshSchool();
}

// ---------------- 分享文案 ----------------
function defaultShare() {
  return {
    title: t("share_title"),
    desc: t("empty_hint"),
    link: location.href.split("#")[0],
    imgUrl: location.origin + "/favicon.svg",
  };
}
function answerShare(aTurn: Turn) {
  const q = aTurn.question || "";
  return {
    title: `${t("share_title")}《${q.slice(0, 14)}》`,
    desc: (aTurn.content || "").slice(0, 48),
    link: location.href.split("#")[0],
    imgUrl: location.origin + "/favicon.svg",
  };
}

// ---------------- 输入框 ----------------
function autoGrow(): void {
  input.style.height = "auto";
  input.style.height = Math.min(input.scrollHeight, 160) + "px";
}
function updateSendState(): void {
  sendBtn.disabled = busy || !input.value.trim();
}

// ---------------- 事件绑定 ----------------
function wireEvents(): void {
  ui.$("composer").addEventListener("submit", (e) => {
    e.preventDefault();
    void send();
  });
  input.addEventListener("input", () => {
    autoGrow();
    updateSendState();
    store.setDraft(sessionId, input.value);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      void send();
    }
  });

  ui.$("btn-new").addEventListener("click", newSession);
  ui.$("btn-script").addEventListener("click", toggleScript);
  ui.$("btn-menu").addEventListener("click", () => togglePanel("drawer-open"));
  ui.$("btn-refs").addEventListener("click", () => togglePanel("refs-open"));
  ui.$("btn-refs-close").addEventListener("click", closePanels);
  ui.$("scrim").addEventListener("click", closePanels);

  // 长按菜单动作
  ui.$("ctx-menu").addEventListener("click", (e) => {
    const act = (e.target as HTMLElement)?.dataset?.act;
    const turn = ctxTurn();
    ui.hideCtxMenu();
    if (!act || !turn) return;
    if (act === "copy") {
      void copyText(turn.content).then(() => ui.toast(t("copied")));
    } else if (act === "share") {
      const text = `${turn.content}\n\n— via Zen-RAG ${location.href.split("#")[0]}`;
      void copyText(text).then(() => ui.toast(t("shared")));
    }
  });
  document.addEventListener("click", (e) => {
    if (!(e.target as HTMLElement)?.closest?.("#ctx-menu")) ui.hideCtxMenu();
  });
  document.addEventListener("scroll", () => ui.hideCtxMenu(), true);

  // 在线/离线
  window.addEventListener("online", () => {
    ui.showNetBanner(null);
    void refreshSessions();
  });
  window.addEventListener("offline", () => ui.showNetBanner(t("net_offline")));
}

// go
void boot();
